import { Box } from "@mantine/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { colors } from "#app/theme/v2/colors";
import { spacing } from "#app/theme/v2/spacing";
import { Loader } from "#components/atoms/Loader/Loader";
import { Text } from "#components/atoms/Typographies/Text";
import { Modal } from "#components/molecules/Modal/Modal";
import { SelectableCard } from "#components/molecules/SelectableCard/SelectableCard";
import { useClient } from "#modules/clients/view/useClient";
import { useCompany } from "#modules/company/view/useCompany";
import { buildExportConsultationMail } from "#modules/quoteRequests/domain/buildExportConsultationMail";
import { MessageTemplatePreview } from "#modules/quoteRequests/view/exportConsultation/MessageTemplatePreview";
import { useMaterialConsultationCsvDownload } from "#modules/quoteRequests/view/exportConsultation/useMaterialConsultationCsvDownload";
import { useExportConsultationZip } from "#modules/quoteRequests/view/useExportConsultationZip";
import { useUser } from "#modules/users/view/useUser";
import { buildMailToLink } from "#shared/helpers/buildMailToLink";
import { formatFullName } from "#shared/helpers/formatFullName";
import { Icon } from "#shared/icons/Icon";
import { AnalyticsEvent, useAnalytics } from "#shared/useAnalytics";
import { QuoteRequestAtom } from "#shared/view/components/AppShell/Atoms";

type ExportFormat = "MATERIALS" | "OUTSOURCING" | "FULL";

type Props = {
  opened: boolean;
  onClose: () => void;
  quoteRequest: NonNullable<QuoteRequestAtom>;
};

export const ExportConsultationModal = ({ opened, onClose, quoteRequest }: Props) => {
  const { t } = useTranslation();
  const analytics = useAnalytics();
  const { client } = useClient(quoteRequest.clientId ?? undefined);
  const { user } = useUser();
  const { company } = useCompany();

  const [format, setFormat] = useState<ExportFormat>("FULL");

  const { downloadCsv, canDownload, isDownloading } =
    useMaterialConsultationCsvDownload(quoteRequest);
  const { exportConsultation, isExportingConsultation } = useExportConsultationZip({
    quoteId: quoteRequest.id,
    onDownloaded: onClose,
  });

  const { subject, body } = buildExportConsultationMail(formatFullName(user), company?.name);

  const handleDownload = () => {
    analytics.capture(AnalyticsEvent.ConsultationExported, {
      quote_request_id: quoteRequest.id,
      format,
    });

    if (format === "FULL") {
      exportConsultation();
      return;
    }

    downloadCsv();
  };

  const handleOpenMailClient = () => {
    analytics.capture(AnalyticsEvent.ConsultationEmailOpened, {
      quote_request_id: quoteRequest.id,
      format,
    });
    window.location.href = buildMailToLink(subject, body);
  };

  return (
    <Modal
      opened={opened}
      title={t("quoteRequestLine.actions.exportConsultation.modal.title")}
      subtitle={[quoteRequest.reference, client?.name].filter(Boolean).join(" · ")}
      closeText={t("quoteRequestLine.actions.exportConsultation.modal.cancel")}
      primaryText={t("quoteRequestLine.actions.exportConsultation.modal.download")}
      onClose={onClose}
      onPrimary={handleDownload}
      primaryDisabled={format === "MATERIALS" ? !canDownload : format !== "FULL"}
      primaryLoading={format === "FULL" ? isExportingConsultation : isDownloading}
      primaryIcon={<Icon name="download" size={16} />}
      orientation="horizontal"
    >
      {isExportingConsultation ? (
        <ExportPreparing />
      ) : (
        <Box style={{ display: "flex", flexDirection: "column", gap: spacing[3] }}>
          <SelectableCard
            title={t("quoteRequestLine.actions.exportConsultation.modal.formats.full.title")}
            description={t(
              "quoteRequestLine.actions.exportConsultation.modal.formats.full.description",
            )}
            selected={format === "FULL"}
            onSelect={() => setFormat("FULL")}
          />
          <SelectableCard
            title={t("quoteRequestLine.actions.exportConsultation.modal.formats.materials.title")}
            description={t(
              "quoteRequestLine.actions.exportConsultation.modal.formats.materials.description",
            )}
            selected={format === "MATERIALS"}
            onSelect={() => setFormat("MATERIALS")}
          />
        </Box>
      )}

      {format === "MATERIALS" && (
        <MessageTemplatePreview
          onOpenMailClient={handleOpenMailClient}
          subject={subject}
          body={body}
        />
      )}
    </Modal>
  );
};

const ExportPreparing = () => {
  const { t } = useTranslation();

  return (
    <Box
      style={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
        gap: spacing[2],
        padding: spacing[6],
      }}
    >
      <Loader size="large" />
      <Text size="small" type="medium">
        {t("quoteRequestLine.actions.exportConsultation.modal.preparing.title")}
      </Text>
      <Text size="small" color={colors.text.secondaryDefault}>
        {t("quoteRequestLine.actions.exportConsultation.modal.preparing.description")}
      </Text>
    </Box>
  );
};
